import { useEffect, useState } from "react"
import { Loader2, X } from "lucide-react"

import AuthAlert from "@/components/tuw/auth/AuthAlert"
import { AccountScreen } from "@/components/tuw/openHouse/AccountScreen"
import WaitingScreen from "@/components/tuw/openHouse/WaitingScreen"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog"
import {
  accountWords, continueSignedIn, enrollAccount, enrollmentFlow, enrollmentWaiting, holdIntent, returnHere,
} from "@/lib/viewingRoom/enrollment"
import { VR } from "@/lib/viewingRoom/wording"

// The short account box (Viewing Room VR-9): what a guest sees over a profile
// or search after clicking Save, Message, Request sample or Video call. The
// click is kept on the server first (POST /join/intent, ViewingRoom::ActionIntent),
// so whichever way the guest goes on (this box's code, the email's link on
// another device, or Sign in) the account resumes it. A browser whose
// enrollment still waits goes straight to its waiting screen; otherwise the
// account form, then the waiting screen. Confirmed, the page goes where the
// server says. Dismissing keeps browsing, the click still kept.
//
// `request` is { action, slug, penName } while open, else null.
// `account` is the server's account props (OpenHouse::AccountProps).
export default function AccountBox({ request, account, onClose }) {
  const [view, setView] = useState({ kind: "loading" }) // loading | account | waiting (waiting) | failed (text)
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    if (!request) return undefined
    let live = true
    setView({ kind: "loading" })

    const open = async () => {
      try {
        await holdIntent({ action: request.action, slug: request.slug, returnTo: returnHere() })
      } catch (err) {
        if (!live) return
        if (err.reason === "signed_in") return window.location.reload()
        const text = err.reason === "too_many_attempts" ? VR.error.intentLimited : VR.error.generic
        return setView({ kind: "failed", text })
      }
      if (!live) return
      try {
        const body = await enrollmentWaiting()
        if (live) setView({ kind: "waiting", waiting: body.waiting })
      } catch (err) {
        if (live) setView(err.status === 404 ? { kind: "account" } : { kind: "failed", text: VR.error.generic })
      }
    }
    open()
    return () => {
      live = false
    }
    // The click itself, not the object the page rebuilds on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request?.action, request?.slug, attempt])

  if (!request) return null
  const words = accountWords(request.action, request.penName)

  // Resolves once the waiting screen shows; a refusal goes back to AccountScreen.
  const enroll = async (fields) => {
    const body = await enrollAccount(fields)
    setView({ kind: "waiting", waiting: body.waiting })
  }

  let body
  if (view.kind === "account") {
    body = <AccountScreen account={account} onPost={enroll} words={words} onSignIn={continueSignedIn} />
  } else if (view.kind === "waiting") {
    body = <WaitingScreen waiting={view.waiting} flow={enrollmentFlow(request.action, request.penName)} />
  } else if (view.kind === "failed") {
    body = (
      <div className="flex flex-col items-start gap-3 px-8 pt-8 pb-6 max-[561px]:px-4">
        <AuthAlert tone="bad">{view.text}</AuthAlert>
        <Button variant="outline" onClick={() => setAttempt((n) => n + 1)}>{VR.error.retry}</Button>
      </div>
    )
  } else {
    body = (
      <div className="flex justify-center px-8 py-16">
        <Loader2 className="size-6 animate-spin text-ink-3" aria-hidden />
      </div>
    )
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent showCloseButton={false} aria-describedby={undefined}
        className="w-[min(560px,94vw)] max-w-[min(560px,94vw)] gap-0 p-0 sm:max-w-[min(560px,94vw)]">
        <DialogTitle className="sr-only">{words.heading}</DialogTitle>
        <button type="button" onClick={onClose} aria-label={VR.account.close}
          className="absolute top-3 right-3 flex size-[34px] items-center justify-center rounded-[10px] text-muted-foreground hover:bg-secondary">
          <X className="size-[17px]" strokeWidth={2} />
        </button>
        {body}
        <div className="flex justify-center border-t border-border px-8 py-3">
          <Button variant="ghost" onClick={onClose}>{VR.account.dismiss}</Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
